import { formatClockTime } from '@/utils/dates';
export function calculateDistanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

import type { OpeningHours } from '@/services/googlePlaces';

export function formatOpeningHours(hours: OpeningHours | undefined, timezone?: string): string {
  if (!hours || !hours.periods) return 'Hours unavailable';

  let today: number;
  if (timezone) {
    const now = new Date();
    const dayInTimezone = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone: timezone
    }).format(now);
    const dayMap: Record<string, number> = {
      'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6
    };
    today = dayMap[dayInTimezone] ?? new Date().getDay();
  } else {
    today = new Date().getDay();
  }

  const todayPeriod = hours.periods.find((p) => p.open?.day === today);

  if (!todayPeriod) return 'Closed today';

  if (hours.openNow) {
    const closeTime = todayPeriod.close?.time;
    if (closeTime) {
      return `Open until ${formatClockTime(closeTime)}`;
    }
    return 'Open now';
  }

  const openTime = todayPeriod.open?.time;
  if (openTime) {
    return `Opens at ${formatClockTime(openTime)}`;
  }

  return 'Hours unavailable';
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

export function getTravelTime(distanceMeters: number, mode: 'walking' | 'transit' | 'driving'): string {
  let speedMps: number;
  switch (mode) {
    case 'walking':
      speedMps = 1.4;
      break;
    case 'transit':
      speedMps = 5.5;
      break;
    case 'driving':
      speedMps = 8.3;
      break;
  }

  const seconds = distanceMeters / speedMps;
  return formatDuration(seconds);
}
